import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { IconButton, Tooltip } from '@material-ui/core';
import ProjectFileIcon from './ProjectFileIcon';
import { icon_paper_plane_bold, icon_paper_plane_def } from 'components/base/images';
import CustomCheckbox from 'components/common/checkbox/CustomCheckbox';
import ViewIcon from 'components/base/icons/ViewIcon';
import { IConvertFile } from 'lib/types/clinic/fileTypes';
import { useProjectFileStore } from 'store/local/projectFile';
import { clinicHtmlTpyes, clinicImageTypes, clinicModelTypes } from 'lib/mapper';
import ProjectSlider from 'components/common/slider/ProjectSlider';
import { useProjectFiles } from 'lib/hooks/project/useProjectFiles';

interface ProjectInfoFilesProps {
  file: IConvertFile;
  useCheckbox?: boolean;
  useSlider?: boolean;
  useViewer?: boolean;
  onChangeCheckbox?: (file: IConvertFile) => void;
  onClickView?: (file: IConvertFile, location: 'partner' | 'group' | 'local') => void;
}

function ProjectFileItem({
  file,
  useCheckbox = false,
  useSlider = false,
  useViewer = false,
  onChangeCheckbox,
  onClickView,
}: ProjectInfoFilesProps) {
  const checkBoxWidth = useCheckbox ? 23 : 0;
  const sliderWidth = useSlider ? 103 : 0;
  const viewerWidth = useViewer ? 50 : 0;
  const informationMinusWidth = 74 + checkBoxWidth + 'px';
  const filenameMinusWidth = sliderWidth + viewerWidth + 'px';
  const [enabledCheckbox, setEnabledCheckbox] = useState<boolean>(false);
  const [enabledView, setEnabledView] = useState<boolean>(false);
  const { isCheckboxChecked, isOwnerStorage, isShareStorage, isLocalStorage } =
    useProjectFileStore();

  const { handleChangeModelOpacity } = useProjectFiles();

  const location = !!file.isLocalStorage ? 'local' : !!file.isOwnerStorage ? 'group' : 'partner';

  const handleChangeOpacity = (value: number) => {
    handleChangeModelOpacity(value, file);
  };

  useEffect(() => {
    if (
      !isCheckboxChecked ||
      (file.isOwnerStorage === isOwnerStorage &&
        file.isShareStorage === isShareStorage &&
        file.isLocalStorage === isLocalStorage)
    ) {
      setEnabledCheckbox(false);
    } else {
      setEnabledCheckbox(true);
    }
  }, [file, isOwnerStorage, isShareStorage, isLocalStorage, isCheckboxChecked]);

  useEffect(() => {
    if (
      clinicModelTypes.includes(file.fileExtension) ||
      clinicImageTypes.includes(file.fileExtension) ||
      clinicHtmlTpyes.includes(file.fileExtension)
    ) {
      setEnabledView(true);
    } else {
      setEnabledView(false);
    }
  }, [file]);

  // useEffect(() => {
  //   console.log('HDS] ProjectFileItem _________ ', file);
  // }, [file]);

  return (
    <StyledProjectFileItem
      data-component-name="ProjectFileItem"
      $informationMinusWidth={informationMinusWidth}
      $filenameMinusWidth={filenameMinusWidth}
    >
      {!!useCheckbox && (
        <label>
          <CustomCheckbox
            marginRight={0}
            width={18}
            disabled={enabledCheckbox}
            checked={!!file.isChecked}
            onClick={e => {
              if (!onChangeCheckbox) return;
              onChangeCheckbox(file);
            }}
          />
        </label>
      )}

      <div className="projectFileItem__contents_box">
        <Tooltip
          className="projectFileItem__filename_tooltip"
          title={file.originName}
          placement="top-start"
          disableHoverListener={false}
          disableFocusListener={true}
          disableTouchListener={true}
        >
          <div className="projectFileItem__information">
            <ProjectFileIcon file={file} />
            <div className="projectFileItem__location">
              <FileLocation locationName="Partner" isLocated={!!file.isShareStorage} />
              <FileLocation locationName="Group" isLocated={!!file.isOwnerStorage} />
              <FileLocation locationName="Local" isLocated={!!file.isLocalStorage} />
            </div>
          </div>
        </Tooltip>
        <div className="projectFileItem__actions">
          {!!useSlider && (
            <div className="projectFileItem__slider_box">
              <ProjectSlider
                value={!!file.opacity ? file.opacity : 0}
                onChange={handleChangeOpacity}
                disabled={!file.isView || typeof file.opacity !== 'number'}
              />
            </div>
          )}
          {!!useViewer && (
            <div className="projectFileItem__viewer_box">
              {!!enabledView && (
                <IconButton
                  className="viewer_icon_box"
                  onClick={() => {
                    if (!onClickView) return;
                    onClickView(file, location);
                  }}
                >
                  <ViewIcon color={!!file.isView ? '#00A6E2' : '#9EA4BE'} />
                </IconButton>
              )}
            </div>
          )}
        </div>
      </div>
    </StyledProjectFileItem>
  );
}

const StyledProjectFileItem = styled.div<{
  $informationMinusWidth: string;
  $filenameMinusWidth: string;
}>`
  width: 100%;
  height: 34px;
  display: flex;
  column-gap: 10px;
  align-items: center;
  padding: 0 12px 0 11px;
  &:hover {
    cursor: default;
  }
  .projectFileItem__contents_box {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .projectFileItem__information {
      display: flex;
      column-gap: 15px;
      .projectFileItem__location {
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 8px;
      }
    }

    .projectFileItem__actions {
      display: flex;
      align-items: center;
      column-gap: 15px;
      .projectFileItem__slider_box {
        width: 83px;
        height: 18px;
        display: flex;
        align-items: center;
      }

      .projectFileItem__viewer_box {
        width: 24px;
        height: 24px;
        .viewer_icon_box {
          padding: 0;
          width: 24px;
          height: 24px;
          border-radius: 50%;
        }
      }
    }
  }
`;

interface FileLocationProps {
  isLocated: boolean;
  locationName: string;
}

function FileLocation({ isLocated, locationName }: FileLocationProps) {
  return (
    <StyledFileLocation data-component-name="FileLocation" $isLocated={isLocated}>
      {/* <img src={!!isLocated ? icon_paper_plane_bold : icon_paper_plane_def} /> */}
      <span className="fileLocation__name">{locationName}</span>
    </StyledFileLocation>
  );
}

const StyledFileLocation = styled.div<{ $isLocated: boolean }>`
  padding: 4px;
  border: 1px solid #e1e1e7;
  border-radius: 4px;
  background-color: ${({ $isLocated }) => (!!$isLocated ? '#E1E1E7' : 'transparent')};
  display: flex;
  align-items: center;
  column-gap: 4px;
  justify-content: center;
  height: 22px;

  .fileLocation__name {
    font-size: 12px;
    font-weight: 400;
    color: ${({ $isLocated }) => (!!$isLocated ? '#666666' : '#CCCCD4')};
  }
`;

export default React.memo(ProjectFileItem);
